import React, { useState } from 'react';

import { Plus } from 'lucide-react';
import { useQuery } from 'react-query';
import { DotBricksLoading } from 'src/common/components';

import { fetchRecipientCohorts } from '../api';
import { EDITION_AUDIENCE_LABEL, RECIPIENT_COHORTS_QUERY_KEY } from '../constants';

import RecipientCohortDetail from './RecipientCohortDetail';
import RecipientCohortFormModal from './RecipientCohortFormModal';

const RecipientCohortsPanel: React.FC = () => {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const { data, isLoading } = useQuery([RECIPIENT_COHORTS_QUERY_KEY], fetchRecipientCohorts);

  const cohorts = data?.data.results ?? [];
  const selected = cohorts.find((c) => c.id === selectedId) ?? cohorts[0] ?? null;

  if (isLoading) return <DotBricksLoading />;

  return (
    <div className="flex h-full overflow-hidden">
      <div className="w-80 flex flex-col flex-shrink-0 overflow-hidden bg-white border-r">
        <div className="flex-shrink-0 p-4 border-b">
          <button
            type="button"
            className="btn btn-primary inline-flex items-center justify-center w-full gap-1 px-4 py-2 text-sm"
            onClick={() => setShowCreate(true)}
          >
            <Plus size={14} />
            Nuevo cohort de personas
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {cohorts.map((cohort) => (
            <button
              key={cohort.id}
              type="button"
              className={`w-full px-4 py-3 text-left border-b border-gray-100 hover:bg-gray-50 ${
                selected?.id === cohort.id ? 'bg-blue-50' : ''
              }`}
              onClick={() => setSelectedId(cohort.id)}
            >
              <div className="flex items-center gap-2">
                <span className="flex-1 text-sm font-semibold text-gray-800 truncate">
                  {cohort.name}
                </span>
                {cohort.kind === 'base' && (
                  <span className="px-2 py-0.5 text-xs font-semibold text-blue-700 bg-blue-50 rounded-full">
                    Base
                  </span>
                )}
              </div>
              <p className="mt-0.5 text-xs text-gray-500">
                {cohort.member_counts.active} activas · {cohort.member_counts.unsubscribed} de baja
                · Lista {EDITION_AUDIENCE_LABEL[cohort.send_list]}
              </p>
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-hidden">
        {selected ? (
          <RecipientCohortDetail
            key={selected.id}
            cohort={selected}
            onArchived={() => setSelectedId(null)}
          />
        ) : (
          <p className="py-12 text-sm text-center text-gray-400">
            Crea un cohort de personas para empezar.
          </p>
        )}
      </div>

      {showCreate && (
        <RecipientCohortFormModal
          onClose={() => setShowCreate(false)}
          onSaved={(created) => {
            setShowCreate(false);
            setSelectedId(created.id);
          }}
        />
      )}
    </div>
  );
};

export default RecipientCohortsPanel;
